import { useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type {
	CollabEditPlugin,
	CollabInitializedMetadata,
	LastOrganicChangeMetadata,
} from '@atlaskit/editor-plugin-collab-edit';

type UseHasEditorContentChanged = {
	editorPresetAPI?: PublicPluginAPI<[CollabEditPlugin]> | null | undefined;
};

export type HasEditorContentChanged = {
	hasEditorContentChangedByAnyUser: boolean;
	hasEditorContentChangedByLocalUserExcludingComments: boolean;
};

/**
 * Hook to determine if the editor content has changed since the editor was initialised
 *
 * @returns An object with two fields, `hasEditorContentChangedByAnyUser` and `hasEditorContentChangedByLocalUserExcludingComments`
 *          hasEditorContentChangedByAnyUser includes changes from remote (i.e. other users in collaborative session), but these can be ghost steps which are problematic
 *          hasEditorContentChangedByLocalUserExcludingComments is true only if the local user has made changes, excluding inline comments (annotation marks)
 */
export const useHasEditorContentChanged = ({
	editorPresetAPI,
}: UseHasEditorContentChanged): HasEditorContentChanged => {
	const [collabInitializedMetadata, setCollabInitializedMetadata] = useState<
		| (CollabInitializedMetadata & Pick<LastOrganicChangeMetadata, 'lastLocalOrganicBodyChangeAt'>)
		| undefined
	>();

	usePluginStateEffect(editorPresetAPI, ['collabEdit'], ({ collabEditState }) => {
		if (!collabEditState?.initialised) {
			return;
		}

		const newCollabInitializedMetadata: CollabInitializedMetadata &
			Pick<LastOrganicChangeMetadata, 'lastLocalOrganicBodyChangeAt'> = {
			collabInitialisedAt: collabEditState.initialised?.collabInitialisedAt,
			firstChangeAfterInitAt: collabEditState.initialised?.firstChangeAfterInitAt,
			firstContentBodyChangeAfterInitAt:
				collabEditState.initialised?.firstContentBodyChangeAfterInitAt,
			lastLocalOrganicBodyChangeAt: collabEditState.initialised?.lastLocalOrganicBodyChangeAt,
		};

		if (!isEqual(collabInitializedMetadata, newCollabInitializedMetadata)) {
			setCollabInitializedMetadata(newCollabInitializedMetadata);
		}
	});

	return useMemo(() => {
		if (!collabInitializedMetadata) {
			return {
				hasEditorContentChangedByAnyUser: false,
				hasEditorContentChangedByLocalUserExcludingComments: false,
			};
		}

		const {
			collabInitialisedAt,
			firstChangeAfterInitAt,
			lastLocalOrganicBodyChangeAt,
			firstContentBodyChangeAfterInitAt,
		} = collabInitializedMetadata;
		const isCollabInitialised = Boolean(collabInitialisedAt);

		const hasEditorContentChangedByAnyUser = isCollabInitialised && Boolean(firstChangeAfterInitAt);
		const hasEditorContentChangedByLocalUserExcludingComments = isCollabInitialised
			? Boolean(lastLocalOrganicBodyChangeAt)
			: Boolean(firstContentBodyChangeAfterInitAt);

		return {
			hasEditorContentChangedByAnyUser,
			hasEditorContentChangedByLocalUserExcludingComments,
		};
	}, [collabInitializedMetadata]);
};
